import React from 'react';
import { 
  Box, 
  Grid, 
  Typography, 
  Paper,
  LinearProgress,
  Chip
} from '@mui/material';
import { lightColours } from '../../styles/colours';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

// Helper function to format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Helper function to format percentage
const formatPercentage = (rate) => {
  return `${rate}%`;
};

// Get color for tax band
const getTaxBandColor = (rate) => {
  const colors = {
    0: '#E8F5E8',    // Light green for 0%
    20: '#FFE4B5',   // Light orange for 20%
    40: '#FFB6C1',   // Light pink for 40%
    45: '#DDA0DD'    // Light purple for 45%
  };
  return colors[rate] || '#E0E0E0';
};

const ComparisonVisualization = ({ comparisonData, config }) => {
  const { salaryA, salaryB, betterOption } = comparisonData;
  const maxSalary = Math.max(salaryA.totalGrossIncome, salaryB.totalGrossIncome);

  const SalaryVisualization = ({ title, data, isBetter = false, position = 'A' }) => {
    const salaryProgress = (data.totalGrossIncome / maxSalary) * 100;
    const takeHomeProgress = (data.takeHome / data.totalGrossIncome) * 100;
    const taxProgress = (data.taxPaid / data.totalGrossIncome) * 100;
    const niProgress = (data.nationalInsurance / data.totalGrossIncome) * 100;
    const pensionProgress = (data.pensionDeduction / data.totalGrossIncome) * 100;
    const totalCompProgress = (data.totalCompensation / maxSalary) * 100;

    return (
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          position: 'relative',
          border: isBetter ? '3px solid #4caf50' : '1px solid #e0e0e0'
        }}
      >
        {isBetter && (
          <Chip 
            icon={<TrendingUpIcon />}
            label="Better Option"
            color="success"
            sx={{ position: 'absolute', top: 16, right: 16 }}
          />
        )}
        
        <Typography variant="h5" component="h3" gutterBottom sx={{ 
          fontWeight: 'bold',
          color: lightColours.darkBlue,
          mb: 3
        }}>
          {title}
        </Typography>

        {/* Salary Bar */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" fontWeight="bold">
              Gross Salary
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {formatCurrency(data.totalGrossIncome)}
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={salaryProgress} 
            sx={{ 
              height: 8, 
              borderRadius: 4,
              backgroundColor: '#E0E0E0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: lightColours.darkBlue
              }
            }}
            role="progressbar"
            aria-label={`Salary progress for ${title}`}
          />
        </Box>

        {/* Tax Bands Visualization */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            Tax Band Breakdown
          </Typography>
          
          {data.bracketDetails && data.bracketDetails.map((bracket, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption">
                  {formatPercentage(bracket.rate)} Tax Band
                </Typography>
                <Typography variant="caption">
                  {formatCurrency(bracket.taxableAmount || 0)}
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={bracket.taxableAmount > 0 ? (bracket.taxableAmount / data.totalGrossIncome) * 100 : 0} 
                sx={{ 
                  height: 6, 
                  borderRadius: 3,
                  backgroundColor: '#F5F5F5',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: getTaxBandColor(bracket.rate)
                  }
                }}
                role="progressbar"
                aria-label={`${bracket.rate}% tax band`}
              />
            </Box>
          ))}
        </Box>

        {/* Take-home vs Deductions */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            Take-Home vs Deductions
          </Typography>
          
          {/* Take-home */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                Take-Home Pay
              </Typography>
              <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                {formatCurrency(data.takeHome)}
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={takeHomeProgress} 
              sx={{ 
                height: 8, 
                borderRadius: 4,
                backgroundColor: '#E8F5E8',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: 'success.main'
                }
              }}
              role="progressbar"
            />
          </Box>

          {/* Tax */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption">
                Income Tax
              </Typography>
              <Typography variant="caption">
                {formatCurrency(data.taxPaid)}
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={taxProgress} 
              sx={{ 
                height: 6, 
                borderRadius: 3,
                backgroundColor: '#F5F5F5',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#FF9800'
                }
              }}
              role="progressbar"
            />
          </Box>

          {/* National Insurance */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption">
                National Insurance
              </Typography>
              <Typography variant="caption">
                {formatCurrency(data.nationalInsurance)}
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={niProgress} 
              sx={{ 
                height: 6, 
                borderRadius: 3,
                backgroundColor: '#F5F5F5',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#9C27B0'
                }
              }}
              role="progressbar"
            />
          </Box>

          {/* Pension Contribution */}
          {data.pensionDeduction > 0 && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption">
                  Pension Contribution
                </Typography>
                <Typography variant="caption">
                  {formatCurrency(data.pensionDeduction)}
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={pensionProgress} 
                sx={{ 
                  height: 6, 
                  borderRadius: 3,
                  backgroundColor: '#F5F5F5',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#2196F3'
                  }
                }}
                role="progressbar"
              />
            </Box>
          )}
        </Box>

        {/* Total Compensation */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            Total Compensation Value
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              Take-Home + Pension Value
            </Typography>
            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              {formatCurrency(data.totalCompensation)}
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={totalCompProgress} 
            sx={{ 
              height: 10, 
              borderRadius: 5,
              backgroundColor: '#E3F2FD',
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'primary.main'
              }
            }}
            role="progressbar"
          />
        </Box>

      </Paper>
    );
  };

  return (
    <Box sx={{ mt: 4 }} data-testid="comparison-visualization">
      <Typography variant="h4" component="h2" gutterBottom sx={{ 
        textAlign: 'center',
        fontWeight: 'bold',
        mb: 4
      }}>
        Visual Salary Comparison
      </Typography>

      <Grid container spacing={4}>
        <Grid size={{ xs: 12, md: 6 }}>
          <SalaryVisualization 
            title="Current Salary Breakdown"
            data={salaryA}
            isBetter={betterOption === 'A'}
            position="A"
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <SalaryVisualization 
            title="New Salary Breakdown"
            data={salaryB}
            isBetter={betterOption === 'B'}
            position="B"
          />
        </Grid>
      </Grid>

    </Box>
  );
};

export default ComparisonVisualization;